// Turns what a player does into content.started / content.progress / content.completed.
//
// Player-agnostic: an adapter (adapters.js) feeds it the player's events. One tracker per
// piece of content; a playback starts at the first play and ends when the media ends or
// the tracker is thrown away.
//
// Ported from the backend's activity-tracking-frontend-guide.md (version 2).

import { uuid } from "./client";

/**
 * What a tracker reports on - see content.js for building one from the library's data.
 *
 * @typedef {object} PlayableContent
 * @property {"video" | "audio" | "podcast"} type "video" or "audio" for library items;
 *   "podcast" for the floating audio player's tracks.
 * @property {number} id Library: locales.<lang>.id of the language version played.
 *   Podcast: the track's id from /audios/playlist.
 * @property {number | null} [startSeconds] locales.<lang>.start_time and end_time: where the
 * @property {number | null} [endSeconds]   episode itself starts and ends, without the intro
 *   and the closing dedications or credits. Pass them even though the player plays the whole
 *   file - the percentage and completion are measured against them.
 */

const MILESTONES = [25, 50, 75];
const COMPLETED_AT = 90; // percent
const CHECKPOINT_SECONDS = 120; // report progress at least this often during long playback
const MIN_STOP_SECONDS = 5; // a stop is worth reporting once this much was played since the last report

export class PlaybackTracker {
  /**
   * @param {import("./client").ActivityClient} client
   * @param {PlayableContent} content
   */
  constructor(client, content) {
    this.client = client;
    this.content = content;
    this.playbackId = null;
    this.duration = 0;
    this.position = 0;
    this.clockMs = 0;
    this.playing = false;
    this.furthest = 0; // furthest point reached while playing, in media seconds
    this.watched = 0; // media seconds actually played; seeking adds nothing
    this.watchedAtLastReport = 0;
    this.passed = new Set();
    this.completed = false;
  }

  play(position, duration) {
    this.setDuration(duration);

    if (this.playbackId === null) {
      this.begin(position);
    }

    this.playing = true;
    this.position = position;
    this.clockMs = performance.now();
  }

  time(position, duration) {
    this.setDuration(duration);
    const nowMs = performance.now();

    if (this.playing && this.playbackId !== null) {
      const played = position - this.position;
      const elapsed = (nowMs - this.clockMs) / 1000;

      // Ordinary playback moves forward, about as fast as the clock - with room for faster
      // playback speeds and for background tabs where updates arrive late. A bigger jump,
      // or any jump back, is a seek: nothing was watched.
      if (played > 0 && played <= elapsed * 2.5 + 1.5) {
        this.watched += played;
        this.furthest = Math.max(this.furthest, position);
        this.afterPlaying(position);
      }
    }

    this.position = position;
    this.clockMs = nowMs;
  }

  seeked(position) {
    this.position = position;
    this.clockMs = performance.now();
  }

  pause(position) {
    this.time(position);
    this.playing = false;
    this.reportStop(position);
  }

  ended(position) {
    this.time(position);
    this.playing = false;
    this.reportStop(position);
    this.playbackId = null; // playing it again is a new playback
  }

  /** The page was hidden, or the player is going away: report how far playback got. */
  leave() {
    this.reportStop(this.position);
    this.client.flush({ unloading: true });
  }

  begin(position) {
    this.playbackId = uuid();
    this.furthest = position;
    this.watched = 0;
    this.watchedAtLastReport = 0;

    // A resumed playback has already passed the milestones behind it.
    const percent = this.percentAt(position) ?? 0;
    this.passed = new Set(MILESTONES.filter((milestone) => percent >= milestone));
    this.completed = percent >= COMPLETED_AT;

    this.report("content.started", position);
  }

  afterPlaying(position) {
    const percent = this.percentAt(this.furthest);

    if (percent !== null) {
      if (!this.completed && percent >= COMPLETED_AT) {
        this.completed = true;
        MILESTONES.forEach((milestone) => this.passed.add(milestone));
        this.report("content.completed", position);
        return;
      }

      const reached = MILESTONES.filter((milestone) => percent >= milestone && !this.passed.has(milestone));
      if (reached.length > 0) {
        reached.forEach((milestone) => this.passed.add(milestone));
        this.report("content.progress", position);
        return;
      }
    }

    if (this.watched - this.watchedAtLastReport >= CHECKPOINT_SECONDS) {
      this.report("content.progress", position);
    }
  }

  reportStop(position) {
    if (this.playbackId !== null && this.watched - this.watchedAtLastReport >= MIN_STOP_SECONDS) {
      this.report("content.progress", position);
    }
  }

  report(event, position) {
    if (this.playbackId === null) {
      return;
    }

    const payload = {
      event,
      content_type: this.content.type,
      content_id: this.content.id,
      playback_id: this.playbackId,
      position_seconds: Math.round(position),
    };

    if (event !== "content.started") {
      const percent = this.percentAt(this.furthest);
      if (percent !== null) {
        payload.percent_watched = Math.round(percent * 10) / 10;
      }
      payload.seconds_watched = Math.round(this.watched);
      this.watchedAtLastReport = this.watched;
    }

    this.client.track(payload);
  }

  /** How far `position` is through the content, 0-100, or null while its length is unknown. */
  percentAt(position) {
    const start = Math.max(0, this.content.startSeconds ?? 0);
    let end = this.content.endSeconds ?? 0;

    if (!(end > start) || (this.duration > 0 && end > this.duration)) {
      end = this.duration;
    }
    if (!(end > start)) {
      return null;
    }

    return Math.min(100, Math.max(0, ((position - start) / (end - start)) * 100));
  }

  setDuration(duration) {
    if (duration !== undefined && Number.isFinite(duration) && duration > 0) {
      this.duration = duration;
    }
  }
}
